import React, { useState, useEffect } from "react";
import {
  FaHourglassHalf,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaMicrophone,
  FaUsers,
  FaCalendarAlt,
  FaChartBar,
  FaAd,
  FaPlay,
  FaCheck,
  FaTimes,
  FaDownload,
  FaGift,
  FaStar,
  FaRocket,
  FaSearch,
  FaCalendar,
  FaClock,
  FaUser,
  FaMapMarkerAlt,
  FaFilter,
  FaMoneyBillWave,
  FaCreditCard,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PaidAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [conferences, setConferences] = useState([]);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [runningConferences, setRunningConferences] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, modified: 0, completed: 0 });
  const [runningStats, setRunningStats] = useState({ running: 0 });

  // Search and filter states
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");

  useEffect(() => {
    const fetchConferences = async () => {
      try {
        setLoading(true);

        // Fetch paid conferences
        const conferencesResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/all`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("Fetched Paid Conferences Data:", conferencesResponse);

        // Handle data shape differences
        const conferencesData = conferencesResponse.data.data?.conferences || conferencesResponse.data.conferences || [];

        setConferences(conferencesData);

        setStats({
          pending: conferencesData.filter((conf) => conf.status === "pending").length,
          approved: conferencesData.filter((conf) => conf.status === "approved").length,
          rejected: conferencesData.filter((conf) => conf.status === "rejected").length,
          modified: conferencesData.filter((conf) => conf.status === "modified").length,
          completed: conferencesData.filter((conf) => conf.status === "completed").length,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching paid conferences:", error);
        toast.error("Failed to load paid conferences");
        setLoading(false);
      }
    };
    fetchConferences();
  }, []);



  useEffect(() => {
    const fetchRunningConferences = async () => {
      try {
        setLoading(true);

        // Fetch running paid conferences
        const runningConferencesResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/all?status=running`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("Fetched Running Paid Conferences Data:", runningConferencesResponse);

        // Handle data shape differences
        const runningConferencesData = runningConferencesResponse.data.data?.conferences || runningConferencesResponse.data.conferences || [];

        setRunningConferences(runningConferencesData || []);
        setRunningStats({
          running: runningConferencesData ? runningConferencesData.filter((conf) => conf.status === "running").length : 0,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching running paid conferences:", error);
        setLoading(false);
      }
    };
    fetchRunningConferences();
  }, []);



  // Filter conferences based on search and filters
  const filteredConferences = conferences.filter((conference) => {
    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesSearch =
        (conference.conferenceId && conference.conferenceId.toLowerCase().includes(searchLower)) ||
        (conference.topic && conference.topic.toLowerCase().includes(searchLower)) ||
        (conference.city && conference.city.toLowerCase().includes(searchLower)) ||
        (conference.state && conference.state.toLowerCase().includes(searchLower)) ||
        (conference.submittedBy?.name && conference.submittedBy.name.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== "all") {
      if (conference.status !== statusFilter) return false;
    }

    // Payment filter
    if (paymentFilter !== "all") {
      if (conference.paymentStatus !== paymentFilter) return false;
    }

    return true;
  });

  // Handle card click to navigate to specific pages
  const handleCardClick = (type) => {
    switch (type) {
      case "pending":
        navigate("/admin/press-conference/paid-conference/pending");
        break;
      case "approved":
        navigate("/admin/press-conference/paid-conference/approved");
        break;
      case "rejected":
        navigate("/admin/press-conference/paid-conference/rejected");
        break;
      case "modified":
        navigate("/admin/press-conference/paid-conference/modified");
        break;
      case "running":
        navigate("/admin/press-conference/paid-conference/running");
        break;
      case "completed":
        navigate("/admin/press-conference/paid-conference/completed");
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="bg-gradient-to-r from-green-600 via-teal-600 to-blue-700 rounded-2xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-2 flex items-center gap-3">
                <FaCreditCard className="text-green-300" />
                Paid Conference Dashboard
              </h1>
              <p className="text-green-100 text-lg">
                Manage and monitor paid press conference requests and their payment status
              </p>
              <div className="mt-4 flex items-center gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-green-100">Total Conferences</span>
                  <div className="text-2xl font-bold text-white">{conferences.length}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-green-100">Filtered Results</span>
                  <div className="text-2xl font-bold text-white">{filteredConferences.length}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-green-100">Total Revenue</span>
                  <div className="text-2xl font-bold text-white">
                    ₹{conferences.reduce((sum, conf) => sum + (conf.paymentAmount || 0), 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        <div
          className="bg-gradient-to-br from-yellow-500 to-amber-600 p-6 rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("pending")}
        >
          <div className="flex flex-col items-center text-center">
            <FaHourglassHalf className="w-12 h-12 mb-3 opacity-90" />
            <p className="text-sm font-medium opacity-90">Pending when user submit the conference and waiting for admin approval</p>
            <p className="text-3xl font-bold mt-2">
              {loading ? "..." : stats.pending || 0}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("approved")}
        >
          <div className="flex flex-col items-center text-center">
            <FaCheckCircle className="w-12 h-12 mb-3 opacity-90" />
            <p className="text-sm font-medium opacity-90">Tracking paid conferences where sended list Task to the reporters</p>
            {/* <p className="text-3xl font-bold mt-2">
              {loading ? "..." : stats.approved || 0}
            </p> */}
          </div>
        </div>


        <div
          className="bg-gradient-to-br from-red-500 to-rose-600 p-6 rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("rejected")}
        >
          <div className="flex flex-col items-center text-center">
            <FaTimesCircle className="w-12 h-12 mb-3 opacity-90" />
            <p className="text-sm font-medium opacity-90">Admin rejected conference</p>
            <p className="text-3xl font-bold mt-2">
              {loading ? "..." : stats.rejected || 0}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-blue-500 to-indigo-600 p-6 rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("running")}
        >
          <div className="flex flex-col items-center text-center">
            <FaPlay className="w-12 h-12 mb-3 opacity-90" />
            <p className="text-sm font-medium opacity-90">Running when user submit proof and proof is WAITING FOR ADMIN APPROVAL by the adminS</p>
            <p className="text-3xl font-bold mt-2">
              {loading ? "..." : runningStats.running || 0}
            </p>
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-purple-500 to-violet-600 p-6 rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          onClick={() => handleCardClick("completed")}
        >
          <div className="flex flex-col items-center text-center">
            <FaCheck className="w-12 h-12 mb-3 opacity-90" />
            <p className="text-sm font-medium opacity-90">Completed when the user submit the proof and proof is approved by the admin</p>
            <p className="text-3xl font-bold mt-2">
              {loading ? "..." : stats.completed || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <FaSearch className="w-5 h-5 text-green-600" />
              Search & Filter
            </h2>
            <p className="text-gray-600 mt-1">Find specific conferences by ID, topic, location, or submitter</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search conferences..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent w-64"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="modified">Modified</option>
              <option value="rejected">Rejected</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
            </select>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Payment Pending</option>
              <option value="failed">Payment Failed</option>
            </select>
            <button
              onClick={() => window.location.reload()}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <FaDownload className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* All Conferences Table */}
      {!loading && filteredConferences.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FaCreditCard className="w-5 h-5" />
              All Paid Conferences ({filteredConferences.length})
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaUser className="w-4 h-4" />
                      Conference ID
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMicrophone className="w-4 h-4" />
                      Topic
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaCalendar className="w-4 h-4" />
                      Date & Time
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                      Location
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMoneyBillWave className="w-4 h-4" />
                      Payment Info
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaUser className="w-4 h-4" />
                      Submitted By
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaClock className="w-4 h-4" />
                      Created At
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaFilter className="w-4 h-4" />
                      Status
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
                  const paginatedConferences = filteredConferences.slice(
                    (currentPage - 1) * rowsPerPage,
                    currentPage * rowsPerPage
                  );
                  return paginatedConferences.map((conference) => (
                    <tr key={conference._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                          {conference.conferenceId}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {conference.topic}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {conference.conferenceDate}
                        </div>
                        <div className="text-xs text-gray-500">
                          {conference.conferenceTime} {conference.timePeriod}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {conference.city}, {conference.state}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <FaUsers className="w-3 h-3 text-gray-500" />
                            <span className="text-sm text-gray-600">
                              {conference.numberOfReporters || 0} Reporters
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaMoneyBillWave className="w-3 h-3 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-900">
                              ₹{conference.paymentAmount || 0}
                            </span>
                          </div>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${conference.paymentStatus === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : conference.paymentStatus === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {conference.paymentStatus || 'pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {conference.submittedBy?.name || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {conference.submittedBy?.organization || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(conference.submittedAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(conference.submittedAt).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${conference.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : conference.status === "rejected"
                              ? "bg-red-100 text-red-800"
                              : conference.status === "modified"
                                ? "bg-orange-100 text-orange-800"
                                : conference.status === "completed"
                                  ? "bg-purple-100 text-purple-800"
                                  : conference.status === "running"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {conference.status === "pending" ? "Under Review" : conference.status}
                        </span>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {(() => {
            const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
            return totalPages > 1 ? (
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredConferences.length)} of {filteredConferences.length} results
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg border border-green-300 text-green-700 bg-white hover:bg-green-50 transition-colors flex items-center gap-2 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 rounded-lg border border-green-300 bg-green-100 text-green-800 font-semibold">
                    {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg border border-green-300 text-green-700 bg-white hover:bg-green-50 transition-colors flex items-center gap-2 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-green-200">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-green-500 animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-green-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-green-500 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-green-700 tracking-wide">
              Loading Paid Conference Dashboard...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we fetch the data.
            </span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredConferences.length === 0 && conferences.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-100 to-teal-100 rounded-full flex items-center justify-center">
            <FaSearch className="w-12 h-12 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Conferences Found
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            Try adjusting your search criteria or filters to find conferences.
          </p>
          <button
            onClick={() => {
              setSearchText("");
              setStatusFilter("all");
              setPaymentFilter("all");
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2 mx-auto"
          >
            <FaFilter className="w-4 h-4" />
            Clear Filters
          </button>
        </div>
      )}

      {/* No Data State */}
      {!loading && conferences.length === 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-100 to-teal-100 rounded-full flex items-center justify-center">
            <FaCreditCard className="w-12 h-12 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No Paid Conferences Yet
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            No paid press conference requests have been submitted yet.
          </p>
        </div>
      )}
    </>
  );
};

export default PaidAdminDashboard;
